import { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import "./SetupGate.css";

const ALLOWED_PATHS = ["/settings", "/admin", "/login"];

/**
 * Full-viewport blocker. Shows when global API keys are not configured.
 * Without keys, the app cannot place orders — DEMO/simulation removed.
 * Users (including admins on non-settings pages) are forced to /settings.
 */
export default function SetupGate({ children }) {
  const { pathname } = useLocation();
  const { data: setup, isLoading } = useQuery({
    queryKey: ["setup-status"],
    queryFn: api.setupStatus,
    refetchInterval: 20000,
    retry: false,
  });

  const allowed = ALLOWED_PATHS.some((p) =>
    p === "/" ? pathname === "/" : pathname.startsWith(p)
  );

  if (isLoading || !setup) return children;
  if (setup.complete) return children;
  if (allowed) {
    return (
      <>
        <SetupBanner setup={setup} />
        {children}
      </>
    );
  }
  return <SetupBlocker setup={setup} />;
}

function SetupBanner({ setup }) {
  const missing = collectMissing(setup);
  return (
    <div
      className="border-l-4 border-[#ff4d5e] bg-[#1A0709] px-4 py-3 mx-5 mt-5 rounded-sm"
      data-testid="setup-banner"
    >
      <div className="flex items-center gap-3">
        <ShieldAlert className="w-4 h-4 text-[#ff4d5e]" />
        <div className="text-xs text-zinc-200 flex-1">
          <b className="text-[#ff4d5e]">DEMO modu kapalı.</b>{" "}
          Sistemi kullanabilmek için API anahtarlarını gir.
          <span className="text-zinc-400"> Eksik: {missing.join(", ")}.</span>
        </div>
      </div>
    </div>
  );
}

function SetupBlocker({ setup }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const missing = collectMissing(setup);

  // Mouse-following spotlight + interactive particles (canvas)
  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    let tx = window.innerWidth * 0.5;
    let ty = window.innerHeight * 0.42;
    let cx = tx;
    let cy = ty;
    let raf = 0;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    window.addEventListener("pointermove", onMove, { passive: true });

    const ctx = canvas.getContext("2d");
    let W = 0;
    let H = 0;
    let ps = [];

    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
      const n = Math.min(64, Math.floor((W * H) / 27000));
      ps = Array.from({ length: n }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.16,
        vy: (Math.random() - 0.5) * 0.16,
        r: Math.random() * 1.5 + 0.5,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const tick = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      wrap.style.setProperty("--mx", cx + "px");
      wrap.style.setProperty("--my", cy + "px");

      if (ctx) {
        ctx.clearRect(0, 0, W, H);
        for (const p of ps) {
          const dx = cx - p.x;
          const dy = cy - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 190 * 190) {
            const k = 1 - Math.sqrt(d2) / 190;
            p.vx += dx * 0.000015 * k * 60;
            p.vy += dy * 0.000015 * k * 60;
          }
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.965;
          p.vy *= 0.965;
          if (p.x < -20) p.x = W + 20;
          if (p.x > W + 20) p.x = -20;
          if (p.y < -20) p.y = H + 20;
          if (p.y > H + 20) p.y = -20;
        }
        for (let i = 0; i < ps.length; i++) {
          const a = ps[i];
          for (let j = i + 1; j < ps.length; j++) {
            const b = ps[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const d = Math.hypot(dx, dy);
            if (d < 116) {
              const mx = (a.x + b.x) / 2 - cx;
              const my = (a.y + b.y) / 2 - cy;
              const near = Math.hypot(mx, my) < 210;
              ctx.strokeStyle = near
                ? `rgba(255,55,68,${(1 - d / 116) * 0.2})`
                : `rgba(255,255,255,${(1 - d / 116) * 0.045})`;
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
        for (const p of ps) {
          const dist = Math.hypot(cx - p.x, cy - p.y);
          const near = dist < 190;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fillStyle = near
            ? `rgba(255,55,68,${0.18 + (1 - dist / 190) * 0.5})`
            : "rgba(255,255,255,0.10)";
          ctx.fill();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const darkexOk = !!setup.exchanges?.darkex;
  const binanceOk = !!setup.exchanges?.binance;
  const okxOk = !!setup.exchanges?.okx;
  const tgOk = !!setup.telegram_token_set;

  return (
    <div ref={wrapRef} className="tx-gate-wrap" data-testid="setup-gate">
      <div className="bg-fx" aria-hidden="true">
        <div className="grid" />
        <div className="grid-hot" />
        <canvas ref={canvasRef} />
        <div className="spot" />
        <div className="b1" />
        <div className="b2" />
      </div>

      <main className="stage">
        <div className="gate">
          <span className="c3" />
          <span className="c4" />

          <div className="gate-head">
            <div className="gate-key">
              <svg viewBox="0 0 24 24">
                <circle cx="8" cy="15" r="4" />
                <path
                  d="M10.8 12.2 20 3M16 7l3 3M14 9l2 2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <div className="eb lbl">Erişim Engellendi</div>
              <h1>API Anahtarı Gerekli</h1>
            </div>
          </div>

          <p className="gate-desc">
            DEMO sürümü kapatıldı. Terminal-EX&apos;i kullanabilmek için en az{" "}
            <span className="ac">Darkex</span> ve karşı borsa (
            <span className="ac">Binance</span> veya <span className="ac">OKX</span>) için
            Futures API anahtarlarını girmen gerekiyor.
          </p>

          <div className="rows">
            <GateRow label="Darkex Futures" ok={darkexOk} required />
            <GateRow label="Binance Futures" ok={binanceOk} required />
            <GateRow label="OKX Futures (opsiyonel)" ok={okxOk} optional />
            <GateRow label="Telegram Bot" ok={tgOk} />
          </div>

          <div className="warn">
            <div className="warn-head">
              <span className="ic">
                <svg viewBox="0 0 24 24">
                  <path d="M12 2 4 5v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V5l-8-3z" />
                </svg>
              </span>
              <span className="t">Güvenlik Uyarısı</span>
              <span className="ln" />
            </div>
            <p className="warn-main">
              API anahtarı oluştururken <u>WITHDRAW (Para Çekme)</u> iznini
              kesinlikle <u>işaretlemeyin.</u>
            </p>
            <p className="warn-sub">
              Terminal-EX, hesabınızdaki bakiyeyi <b>hiçbir koşulda çekemez</b>{" "}
              — yalnızca sizin adınıza pozisyon açıp kapatır. Para çekme yetkisi
              bu sistemin çalışması için asla gerekli değildir.
            </p>
          </div>

          <Link
            to="/settings"
            data-testid="setup-gate-cta"
            className="go-btn"
            onClick={(e) => {
              e.preventDefault();
              navigate("/settings");
            }}
          >
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 13a7.6 7.6 0 0 0 0-2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-1.7-1L15 2.5h-4l-.3 2.5a7 7 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7.6 7.6 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 1.7 1l.3 2.5h4l.3-2.5a7 7 0 0 0 1.7-1l2.4 1 2-3.4z" />
            </svg>
            Ayarlar&apos;a Git
            <svg viewBox="0 0 24 24">
              <path
                d="M5 12h14M13 6l6 6-6 6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>

          <div className="gate-foot">
            Eksik: <b>{missing.join(" · ") || "—"}</b>
          </div>
        </div>
      </main>
    </div>
  );
}

function GateRow({ label, ok, required = false, optional = false }) {
  const cls = ["row"];
  if (ok) cls.push("done");
  else if (required) cls.push("req");
  else if (optional) cls.push("opt");

  return (
    <div className={cls.join(" ")}>
      <span className="nm">{label}</span>
      {ok ? (
        <span className="stat ready">
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" />
            <path
              d="M8.5 12.5 11 15l4.5-5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>{" "}
          Hazır
        </span>
      ) : (
        <span className="stat empty">
          <svg viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" />
            <path d="M15 9l-6 6M9 9l6 6" strokeLinecap="round" />
          </svg>{" "}
          Boş
        </span>
      )}
    </div>
  );
}

function collectMissing(setup) {
  const m = [];
  if (!setup.exchanges?.darkex) m.push("Darkex");
  if (!setup.exchanges?.binance && !setup.exchanges?.okx) m.push("Karşı Borsa");
  if (!setup.telegram_token_set) m.push("Telegram");
  return m;
}
